import { apiClient } from '@/lib/api/apiClient';
import { endpoints } from '@/lib/api/apiEndpoints';
import type { LoginCredentials, RegisterData, AuthResponse, User } from '../types/auth.types';

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

interface BackendUserDto {
  id: string; // Guid as string in JSON
  username: string;
  email: string;
  displayName: string;
  role: string;
}

interface BackendAuthResponse {
  token: string;
  user: BackendUserDto;
}

export const authService = {
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      const email = (credentials.email ?? '').toString().trim().toLowerCase();
      const password = credentials.password ?? '';
      const loginPayload = {
        email,
        password: password.toString()
      };
      
      const response = await apiClient.post<ApiResponse<BackendAuthResponse>>(
        endpoints.auth.login(),
        loginPayload
      );
      
      // Check if response structure is correct
      if (!response.data) {
        throw new Error('Invalid response from server');
      }
      
      // Handle both wrapped (success/data) and camelCase/PascalCase from backend
      const responseData = response.data as unknown as Record<string, unknown>;
      const success = responseData?.success === true || responseData?.Success === true;
      const payload = (responseData?.data ?? responseData?.Data) as Record<string, unknown> | null;
      const msg = (responseData?.message ?? responseData?.Message) as string | undefined;

      if (!success || !payload) {
        throw new Error(msg || 'فشل تسجيل الدخول');
      }

      const token = (payload?.token ?? payload?.Token) as string | undefined;
      const userObj = (payload?.user ?? payload?.User) as Record<string, unknown> | undefined;
      if (!token || !userObj) {
        throw new Error(msg || 'استجابة الخادم غير صحيحة');
      }

      const roleStr = String(userObj?.role ?? userObj?.Role ?? 'Player').trim();
      let userRole: User['role'] = 'Player';
      if (roleStr === 'SuperAdmin' || roleStr === '4') userRole = 'SuperAdmin';
      else if (roleStr === 'Admin' || roleStr === '3') userRole = 'Admin';
      else if (roleStr === 'Moderator' || roleStr === '2') userRole = 'Moderator';
      else if (roleStr === 'ContentEditor' || roleStr === '1') userRole = 'ContentEditor';

      return {
        token,
        user: {
          id: String(userObj?.id ?? userObj?.Id ?? ''),
          username: String(userObj?.username ?? userObj?.Username ?? ''),
          email: String(userObj?.email ?? userObj?.Email ?? ''),
          displayName: String(userObj?.displayName ?? userObj?.DisplayName ?? ''),
          role: userRole,
        },
      };
    } catch (error: any) {
      console.error('Login error details:', error);
      
      // Network error handling
      if (!error.response) {
        const { API_BASE_URL } = await import('@/lib/api/apiClient');
        const showUrl = API_BASE_URL && !API_BASE_URL.includes('localhost');
        if (error.code === 'ERR_NETWORK' || error.message?.includes('Network Error')) {
          throw new Error(showUrl ? `لا يمكن الاتصال بالخادم. تأكد أن Backend يعمل على ${API_BASE_URL}` : 'لا يمكن الاتصال بالخادم. تأكد أن الخادم يعمل.');
        }
        throw new Error('خطأ في الاتصال بالشبكة. تحقق من اتصالك بالإنترنت.');
      }
      
      // HTTP error handling (401 = wrong credentials, etc.)
      if (error.response) {
        const errorData = error.response.data as Record<string, unknown> | undefined;
        const errorMessage =
          (errorData?.message ?? errorData?.Message ?? errorData?.error ?? errorData?.Error) as string | undefined;
        throw new Error(errorMessage || 'فشل تسجيل الدخول');
      }
      
      throw error;
    }
  },

  async register(data: RegisterData): Promise<AuthResponse> {
    const response = await apiClient.post<ApiResponse<BackendAuthResponse>>(
      endpoints.auth.register(),
      data
    );
    
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Registration failed');
    }
    
    // Map backend response to frontend format
    const backendData = response.data.data;
    return {
      token: backendData.token,
      user: {
        id: backendData.user.id,
        username: backendData.user.username,
        email: backendData.user.email,
        displayName: backendData.user.displayName,
        role: backendData.user.role as User['role'],
      },
    };
  },

  async logout(): Promise<void> {
    try {
      await apiClient.post(endpoints.auth.logout());
    } catch (error) {
      console.error('Logout API call failed:', error);
      // Don't throw - we still want to clear local state
    }
  },

  async checkSession(): Promise<boolean> {
    try {
      const response = await apiClient.get<{ success: boolean; valid: boolean }>(
        '/api/auth/session'
      );
      return response.data.success && response.data.valid;
    } catch (error) {
      return false;
    }
  },
};
